import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook that creates a typewriter (type-in) effect for streaming text.
 *
 * Instead of showing each streamed chunk instantly, this hook buffers the
 * target text and reveals it character by character at a configurable speed,
 * producing a smooth typing animation.
 *
 * When streaming ends (finished=true), any remaining buffered text is flushed
 * once the animation cursor has caught up, so the final content is never delayed
 * but the typing animation still plays to completion.
 *
 * @param {Object} params
 * @param {string}  params.text      - The full accumulated text so far (grows with each stream chunk).
 * @param {boolean} params.finished  - Whether the stream has finished.
 * @param {boolean} [params.enabled=true] - Set to false to bypass the effect and show text instantly.
 * @param {boolean} [params.paused=false] - When true the animation is suspended (cursor doesn't advance). Useful for sequencing multiple typewriters so each waits for the previous one to finish.
 * @param {Function} [params.onComplete] - Called once when the animation has fully caught up to the target text AND the interaction is finished. Used to signal the next typewriter in the queue.
 * @param {number}  [params.speed=12] - Milliseconds between each revealed character.
 * @returns {string} The progressively revealed text.
 */
const useTypewriterEffect = ({
    text = '',
    finished = false,
    enabled = true,
    paused = false,
    onComplete,
    speed = 12,
}) => {
    const [displayedText, setDisplayedText] = useState(text);
    const rafRef = useRef(null);
    const lastTimestampRef = useRef(null);
    const cursorRef = useRef(text.length);
    const targetTextRef = useRef(text);
    const prevTextRef = useRef(text);
    const onCompleteRef = useRef(onComplete);
    const completeFiredRef = useRef(false);
    // Track whether the text was already finished when it first appeared.
    // When true, we skip the animation entirely (e.g. pre-loaded content).
    const wasFinishedOnArrivalRef = useRef(finished && text.length > 0);

    // Always keep callbacks up to date without triggering effects
    onCompleteRef.current = onComplete;

    // Always keep target up to date
    targetTextRef.current = text;

    // If text arrived already finished (pre-loaded), show it instantly
    // and keep skipping until a real reset happens (new segment).
    if (wasFinishedOnArrivalRef.current) {
        if (cursorRef.current !== text.length) {
            cursorRef.current = text.length;
        }
    }

    // Fire onComplete once when the cursor has caught up AND the segment is finished
    useEffect(() => {
        if (!enabled) return;

        const caughtUp = cursorRef.current >= text.length && text.length > 0;
        const shouldFire = (finished || wasFinishedOnArrivalRef.current) && caughtUp;

        if (shouldFire && !completeFiredRef.current) {
            completeFiredRef.current = true;
            onCompleteRef.current?.();
        }
    }, [enabled, finished, displayedText, text]);

    // Detect text resets: when the new text is shorter than what we've already
    // revealed it means a new segment started. Reset the cursor so the
    // typewriter restarts from the beginning of the new segment.
    useEffect(() => {
        if (!enabled) return;

        const prev = prevTextRef.current;
        prevTextRef.current = text;

        // A reset happens when the new text doesn't start with what we had before
        // (e.g. previous segment was "Perfecto…" and now it's "He encontrado…")
        const isReset = prev.length > 0 && text.length < prev.length;
        const isNewSegment =
            prev.length > 0 && !text.startsWith(prev.slice(0, Math.min(prev.length, text.length)));

        if (isReset || isNewSegment) {
            // New segment arrived — clear the pre-loaded flag so animation runs
            wasFinishedOnArrivalRef.current = false;
            completeFiredRef.current = false;

            // Cancel ongoing animation
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            cursorRef.current = 0;
            lastTimestampRef.current = null;
            setDisplayedText('');
        }
    }, [enabled, text]);

    // When the effect is disabled, or text was pre-loaded, show raw text
    useEffect(() => {
        if (!enabled || wasFinishedOnArrivalRef.current) {
            setDisplayedText(text);
            cursorRef.current = text.length;
        }
    }, [enabled, text]);

    const tick = useCallback(
        (timestamp) => {
            if (!lastTimestampRef.current) lastTimestampRef.current = timestamp;
            const elapsed = timestamp - lastTimestampRef.current;

            if (elapsed >= speed) {
                const target = targetTextRef.current;
                const charsToAdvance = Math.max(1, Math.floor(elapsed / speed));
                const nextCursor = Math.min(cursorRef.current + charsToAdvance, target.length);

                if (nextCursor !== cursorRef.current) {
                    cursorRef.current = nextCursor;
                    setDisplayedText(target.slice(0, nextCursor));
                }

                lastTimestampRef.current = timestamp;
            }

            // Keep ticking if there's more to reveal
            if (cursorRef.current < targetTextRef.current.length) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                rafRef.current = null;
                lastTimestampRef.current = null;
            }
        },
        [speed],
    );

    // Start/continue animation when target text grows (respects paused)
    useEffect(() => {
        if (!enabled || wasFinishedOnArrivalRef.current || paused) return;

        if (cursorRef.current < text.length && !rafRef.current) {
            rafRef.current = requestAnimationFrame(tick);
        }

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [enabled, paused, text, tick]);

    // Flush remaining text instantly when finished
    useEffect(() => {
        if (finished && enabled && cursorRef.current >= targetTextRef.current.length) {
            // Only flush if the animation has already caught up — this
            // prevents premature flush when `finished` is set mid-animation
            // (e.g. when onTextDone marks an interaction as finished while
            // deltas are still being revealed).
            setDisplayedText(text);
        }
    }, [finished, enabled, text]);

    return enabled ? displayedText : text;
};

export default useTypewriterEffect;
